import { EMPTY_CONTENT_FALLBACK } from '../common/constants'

export const pluralize = (count: number, singular: string, plural: string): string => {
  return `${count} ${count === 1 ? singular : plural}`
}

const isNullOrEmpty = (value: string): boolean => {
  return value == null || value?.trim()?.length === 0
}

export const safeIsNullOrEmpty = (value: unknown): boolean => {
  return typeof value === 'string' ? isNullOrEmpty(value) : value == null
}

export const safeIsTrimmedNumeric = (value: unknown): boolean => {
  return /^[0-9]*$/.test((value + '').trim())
}

export const capitalizeFirstLetter = (text: string): string => {
  if (safeIsNullOrEmpty(text)) {
    return ''
  }

  return text[0].toUpperCase() + text.slice(1).toLowerCase()
}

export const padNumWithZero = (num: number, length: number) => {
  // optional chaining + fallback for browsers that don't support padStart
  return num.toString()?.padStart?.(length, '0') ?? num.toString()
}

export const camelToSnakeCase = (str: string) => str.replace(/[A-Z]/g, (letter: string) => `_${letter.toLowerCase()}`)

export const parseDDMMYYYY = (dateStr: string): string => {
  const [day, month, year] = dateStr.split('-').map(Number)
  const date = new Date(year, month - 1, day)
  return date.toString()
}

export const parseFinnishDateTimeToStringToDate = (dateStr: string): Date => {
  const [datePart, timePart] = dateStr.split(' ')
  if (!datePart) return new Date()
  const [day, month, year] = datePart.split('-').map(Number)
  const [hour, minute] = timePart ? timePart.split(':').map(Number) : [0, 0]

  // JS months are 0-indexed!
  const localDate = new Date(year, month - 1, day, hour, minute, 0)
  return localDate
}

export const parseDDMMYYYYcomma = (dateStr: string): string => {
  const [day, month, year] = dateStr.split('.').map(Number)
  const date = new Date(year, month - 1, day)
  return date.toString()
}

export const parseDDMMYYYYHHcomma = (dateStr: string): Date => {
  const [datePart, timePart] = dateStr.split(' ')
  if (!datePart) return new Date()
  const [day, month, year] = datePart.split('.').map(Number)
  const [hour, minute] = timePart ? timePart.split(':').map(Number) : [0, 0]

  // JS months are 0-indexed!
  const localDate = new Date(year, month - 1, day, hour, minute, 0)
  return localDate
}

export const parseDateTimeToStringToDate = (dateStr: string): Date => {
  const [datePart, timePart] = dateStr.split(' ')
  if (!datePart) return new Date()
  const [day, month, year] = datePart.split('.').map(Number)
  const [hour, minute] = timePart ? timePart.split(':').map(Number) : [0, 0]

  // JS months are 0-indexed!
  const localDate = new Date(year, month - 1, day, hour, minute, 0)
  return localDate
}

export const parseDateTimeToString = (dateTimeString: string): string => {
  if (!dateTimeString) return ''

  const localDate = parseDateTimeToStringToDate(dateTimeString)
  const dateIsValid = !isNaN(localDate.getTime()) ? true : false
  return `${dateIsValid ? localDate.toLocaleDateString('en-GB') : '00:00:00'} ${
    dateIsValid
      ? localDate.toLocaleTimeString('en-US', {
          hour12: false,
          hour: '2-digit',
          minute: '2-digit',
        })
      : ''
  }`
}

export const parseFinnishDateTimeToString = (dateTimeString: string): string => {
  if (!dateTimeString) return ''

  const localDate = parseFinnishDateTimeToStringToDate(dateTimeString)
  const dateIsValid = !isNaN(localDate.getTime()) ? true : false
  return `${dateIsValid ? localDate.toLocaleDateString('en-GB') : '00:00:00'} ${
    dateIsValid
      ? localDate.toLocaleTimeString('en-US', {
          hour12: false,
          hour: '2-digit',
          minute: '2-digit',
        })
      : ''
  }`
}

export const dateTimeToISOString = (dateTime: string | undefined): string => {
  if (dateTime == null) {
    return EMPTY_CONTENT_FALLBACK
  }
  const zDateTime = dateTime.toUpperCase().endsWith('Z') ? dateTime : `${dateTime}Z`
  return new Date(zDateTime).toISOString()
}

export const dateTimeToLocalDateTimeStringFromDate = (dateTime: Date | undefined): string => {
  if (!dateTime) {
    return EMPTY_CONTENT_FALLBACK
  }

  const dateIsValid = !isNaN(dateTime.getTime())
  return `${dateIsValid ? dateTime.toLocaleDateString('en-GB') : '00:00:00'}${
    dateIsValid ? ' ' + dateTime.toLocaleTimeString('en-US', { hour12: false }) : ''
  }`
}

export const dateTimeToDashDateString = (dateTime: string | undefined, locale?: string): string => {
  if (!dateTime) {
    return ''
  }
  const lng = 'en-GB'
  return new Date(dateTime).toLocaleDateString(lng)
}

export const dateTimeToDashDateTimeString = (dateTime: string | undefined): string => {
  if (!dateTime) {
    return EMPTY_CONTENT_FALLBACK
  }
  const zDateTime = dateTime.toUpperCase().endsWith('Z') ? dateTime : `${dateTime}Z`
  const datePart = new Date(zDateTime)
  const dateIsValid = !isNaN(datePart.getTime()) ? true : false
  return `${dateIsValid ? datePart.toLocaleDateString('en-GB') : '00:00:00'} ${
    dateIsValid ? new Date(zDateTime).toLocaleTimeString('en-US', { hour12: false }) : ''
  }`
}

export const dateTimeToLocalDateString = (dateTime: string | undefined, locale?: string): string => {
  if (!dateTime) {
    return ''
  }
  const lng = 'fi-FI'
  return new Date(dateTime).toLocaleDateString(lng)
}

export const dateTimeToLocalDateTimeString = (dateTime: string | undefined): string => {
  if (!dateTime) {
    return EMPTY_CONTENT_FALLBACK
  }
  const zDateTime = dateTime.toUpperCase().endsWith('Z') ? dateTime : `${dateTime}Z`
  const datePart = new Date(zDateTime)
  const dateIsValid = !isNaN(datePart.getTime()) ? true : false
  return `${dateIsValid ? datePart.toLocaleDateString('fi-FI') : '00:00:00'} ${
    dateIsValid ? new Date(zDateTime).toLocaleTimeString('en-US', { hour12: false }) : ''
  }`
}

export const getCurrentIsoDate = () => {
  return new Date().toISOString().split('T')[0]
}

export const getCurrentIsoTimestamp = () => {
  return new Date().toISOString()
}

export const decodeHtml = (html: string) => {
  const txt = document.createElement('textarea')
  txt.innerHTML = html
  //Do it twice as for example ä get's double encoded in feedback comment to &aml;auml;
  txt.innerHTML = txt.value
  return txt.value
}

export const getHtmlText = (html: string): string => {
  const el = document.createElement('div')
  el.innerHTML = html
  return el.innerText
}

export const isHtmlTextEmpty = (html: string): boolean => isNullOrEmpty(getHtmlText(html))

export const boldSubstrings = (text: string, substring: string): string => {
  const regex = new RegExp(substring, 'gi')
  const matchedParts = text.match(regex)
  const otherParts = text.split(regex)
  return matchedParts == null
    ? text
    : matchedParts.reduce((formattedText: string, currMatch: string, index: number) => {
        return formattedText + `<b>${currMatch}</b>` + otherParts?.[index + 1]
      }, otherParts[0])
}

export const compareStrings = (stringA: string, stringB: string) => {
  const locale = localStorage.getItem('i18nextLng') ?? 'en'
  return stringA.localeCompare(stringB, [locale, 'en'])
}

export const trimDoubleQuotes = (text: string): string => {
  if (text.startsWith('"')) {
    return trimDoubleQuotes(text.substring(1))
  } else if (text.endsWith('"')) {
    return trimDoubleQuotes(text.substring(0, text.length - 1))
  }
  return text
}

export const softBreakToEmail = (email: string): string => {
  return email.replace(/([@.+]+)/g, '$1<wbr>')
}
